import { motion } from "framer-motion";
import { Wrench, RefreshCw, Gauge, Search, Settings, ShieldCheck } from "lucide-react";
import { useSlowFactor } from "@/hooks/useSlowFactor";
import { useLightbox } from "@/components/Lightbox";
import { useLanguage } from "@/i18n/LanguageContext";

const serviceImages = [
  {
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_boost-shop-pro/artifacts/w84htqbr_IMG_9060.webp",
  },
  {
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_boost-shop-pro/artifacts/c6b5zufr_IMG_3386.webp",
  },
  {
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_boost-shop-pro/artifacts/lew20myd_IMG_9057.webp",
  },
];

const icons = [Wrench, RefreshCw, Gauge, Search, Settings, ShieldCheck];

export const Services = () => {
  const slow = useSlowFactor();
  const { open } = useLightbox();
  const { t } = useLanguage();

  const services = t.services.items.map((it, i) => ({ ...it, icon: icons[i] }));

  const card = (s, i) => (
    <motion.div
      key={s.title}
      data-testid={`service-card-${i}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.72 * slow, delay: (i % 3) * 0.14 * slow }}
      className="group border-r border-b border-zinc-800 p-8 lg:p-10 hover:bg-zinc-900/60 transition-colors relative"
    >
      <div className="absolute top-0 left-0 w-0 h-1 bg-red-600 group-hover:w-full transition-all duration-300 shadow-[0_0_15px_rgba(239,68,68,0.6)]" />
      <s.icon size={32} strokeWidth={1.5} className="text-red-500 mb-6" />
      <h3 className="font-display font-bold text-xl uppercase tracking-tight text-white mb-3">{s.title}</h3>
      <p className="text-zinc-300 leading-relaxed">{s.desc}</p>
    </motion.div>
  );

  return (
    <section id="services" data-testid="services-section" className="py-16 sm:py-24 lg:py-32 carbon-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-red-500 mb-4">{t.services.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight text-white">
            {t.services.heading}
          </h2>
        </div>

        {/* Row 1 */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-zinc-800">
          {services.slice(0, 3).map((s, i) => card(s, i))}
        </div>

        {/* Images row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3">
          {serviceImages.map((img, i) => (
            <motion.div
              key={i}
              data-testid={`service-image-${i}`}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 * slow, delay: i * 0.1 * slow }}
            >
              <div
                onClick={() => open(img.url, t.services.caption)}
                className="relative overflow-hidden group photo-frame cursor-pointer aspect-[16/10]"
              >
                <img
                  src={img.url}
                  alt={t.services.caption}
                  className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-105"
                />
              </div>
              <p data-testid={`service-image-caption-${i}`} className="text-center text-lg font-extrabold uppercase tracking-[0.2em] text-red-500 mt-3">
                {t.services.caption}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Row 2 */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-zinc-800">
          {services.slice(3).map((s, i) => card(s, i + 3))}
        </div>
      </div>
    </section>
  );
};
